import { ArrowRight, Handshake, Layers, MapPin, Rocket } from "lucide-react";
import { ChapterTag, FadeUp, WA_LINK } from "@/components/Reveal";

const THESIS = [
  {
    icon: MapPin,
    title: "Tier-2 first mover",
    copy: "Metro food markets are saturated. Ranchi's appetite is exploding — and we already own the home turf.",
  },
  {
    icon: Layers,
    title: "Three engines, one chain",
    copy: "Dine-in, delivery and institutional feeding share sourcing, chefs and kitchens. Costs stay low, quality stays high.",
  },
  {
    icon: Handshake,
    title: "Recurring B2B revenue",
    copy: "Nine hospital contracts mean predictable daily orders that no algorithm change or rainy season can switch off.",
  },
  {
    icon: Rocket,
    title: "A playbook that replicates",
    copy: "Five brands launched from one kitchen. The model is proven, packaged and ready to scale city by city.",
  },
];

const DEPLOYMENT = ["More cloud kitchens", "More hospital contracts", "More flagship outlets"];

export default function InvestorDeck() {
  return (
    <section id="invest" data-testid="investor-deck" className="relative bg-cream px-5 py-24 text-obsidian sm:px-8 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <ChapterTag index="04" label="The Opportunity" dark={false} />

        <div className="mt-12 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <FadeUp className="lg:col-span-8">
            <h2 className="font-display text-2xl font-semibold leading-[1.12] tracking-tight sm:text-3xl lg:text-4xl" data-testid="investor-headline">
              Why smart money is{" "}
              <span className="font-serif italic font-medium text-flame">pulling up a chair.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.12} className="lg:col-span-4">
            <p className="text-base leading-relaxed text-slate-600">
              We're raising growth capital to take a fifteen-year, profit-tested food operation
              and press the accelerator. Here's the short version of the deck.
            </p>
          </FadeUp>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4" data-testid="investor-thesis-grid">
          {THESIS.map((t, i) => (
            <FadeUp key={t.title} delay={i * 0.1}>
              <div className="group h-full rounded-3xl border border-obsidian/10 bg-white p-8 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-2 hover:border-flame/40 hover:shadow-[0_24px_60px_rgba(224,90,43,0.14)]">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-flame/10 text-flame transition-colors duration-300 group-hover:bg-flame group-hover:text-cream">
                  <t.icon size={22} strokeWidth={1.8} />
                </span>
                <h3 className="mt-6 font-display text-xl font-semibold tracking-tight">{t.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{t.copy}</p>
              </div>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.15} className="mt-14">
          <div className="overflow-hidden rounded-3xl bg-obsidian p-9 text-cream sm:p-12" data-testid="capital-deployment-band">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div>
                <span className="font-mono text-xs uppercase tracking-[0.3em] text-saffron">Where your capital goes</span>
                <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-3">
                  {DEPLOYMENT.map((d, i) => (
                    <span key={d} className="flex items-center gap-5">
                      <span className="font-display text-lg font-semibold sm:text-2xl">{d}</span>
                      {i < DEPLOYMENT.length - 1 && <ArrowRight size={20} className="text-flame" />}
                    </span>
                  ))}
                </div>
              </div>
              <a
                data-testid="investor-deck-cta-button"
                href={WA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-3 rounded-full bg-saffron px-7 py-4 font-display text-base font-semibold text-obsidian transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-flame hover:text-cream"
              >
                Start the Conversation
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
